import { all, call, fork, put, race, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { getCountriesRequest } from '@actions/countries';
import * as actions from '@actions/user';
import { changePasswordRequest, inviteUser, updateUser, userSignUp } from '@api/user';
import { setUser } from '@helpers/user';
import { history, settingsRoutes } from '@routes';
import { handleChangePasswordSaga } from '@sagas/user/handleChangePasswordSaga';
import { handleGetUserAccountSaga } from '@sagas/user/handleGetUserAccountSaga';
import { handleGetUserSaga } from '@sagas/user/handleGetUserSaga';
import { handleVerifyChangePasswordSaga } from '@sagas/user/handleVerifyChangePasswordSaga';
import {
  CHANGE_PASSWORD_REQUEST,
  CHANGE_SETTINGS_PASSWORD_REQUEST,
  CREATE_USER_REQUEST,
  FORGOT_PASSWORD_REQUEST,
  GET_USER_ACCOUNT,
  INVITE_USER_REQUEST,
  UNREAD_MESSAGE_COUNT_REQUEST,
  UNREAD_MESSAGE_COUNT_START,
  UNREAD_MESSAGE_COUNT_STOP,
  USER_GET_REQUEST,
  USER_LOGOUT_REQUEST,
  VERIFY_CHANGE_PASSWORD_REQUEST,
} from '@store/user/user.const';

import { forgotPasswordSaga } from './forgotPasswordSaga';
import { handleUnreadMessagesRequestSaga } from './handleUnreadMessagesRequestSaga';
import { logOutSaga } from './logoutSaga';
import { pollUnreadMessagesSaga } from './pollUnreadMessagesSaga';

export const handleCreateUserRequest = function* ({ payload }) {
  try {
    const { user, existing } = payload;
    const response = !existing
      ? yield call(userSignUp, user)
      : yield call(updateUser, user.id, user);
    if (response.isAxiosError) return yield put(actions.createUserFailure(response.message));
    if (!existing) {
      if (response.email && !response.id)
        return yield put(actions.createUserFailure(response.email));
      const { id } = response.user;
      const { token, refresh } = response;
      setUser({ id, token, refresh });
    } else {
      const { id } = response.user;
      const { token, refresh } = response;
      setUser({ id, token, refresh });
    }

    yield put(actions.createUserSuccess(response));
    yield put(getCountriesRequest());
  } catch (e) {
    yield put(actions.createUserFailure(e));
  }
};

export const handleChangeSettingsPassword = function* ({ payload }) {
  try {
    const response = yield call(changePasswordRequest, payload);
    if (!response.status) return yield put(actions.changeSettingsPasswordFailure(response));
    yield put(actions.changeSettingsPasswordSuccess(response));
  } catch (error) {
    yield put(actions.changeSettingsPasswordFailure(error));
  }
};

export const handleInviteUserRequest = function* ({ payload }) {
  try {
    const response = yield call(inviteUser, payload);
    if (response.isAxiosError) return yield put(actions.inviteUserFailure(response));
    yield put(actions.inviteUserSuccess(response));
    yield history.push(settingsRoutes.USERS_DETAIL.make(response.user));
  } catch (error) {
    yield put(actions.inviteUserFailure(error));
  }
};

export const watchPollUnreadMessagesSaga = function* () {
  while (yield take(UNREAD_MESSAGE_COUNT_START)) {
    yield race([call(pollUnreadMessagesSaga), take(UNREAD_MESSAGE_COUNT_STOP)]);
  }
};

export default all([
  fork(watchPollUnreadMessagesSaga),
  takeEvery(USER_LOGOUT_REQUEST, logOutSaga),
  takeEvery(USER_GET_REQUEST, handleGetUserSaga),
  takeEvery(CREATE_USER_REQUEST, handleCreateUserRequest),
  takeEvery(GET_USER_ACCOUNT, handleGetUserAccountSaga),
  takeEvery(FORGOT_PASSWORD_REQUEST, forgotPasswordSaga),
  takeLatest(CHANGE_PASSWORD_REQUEST, handleChangePasswordSaga),
  takeLatest(VERIFY_CHANGE_PASSWORD_REQUEST, handleVerifyChangePasswordSaga),
  takeEvery(CHANGE_SETTINGS_PASSWORD_REQUEST, handleChangeSettingsPassword),
  takeEvery(INVITE_USER_REQUEST, handleInviteUserRequest),
  takeLatest(UNREAD_MESSAGE_COUNT_REQUEST, handleUnreadMessagesRequestSaga),
]);
